import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import {
  fetchBlogs,
  selectAllBlogs,
  selectBlogsStatus,
  selectBlogsError,
  selectBlogsTotalPages,
  selectBlogsCurrentPage,
  selectAllTags,
  setCurrentPage,
} from "../redux/blogSlice";

const Blog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");

  // Redux state
  const blogs = useSelector(selectAllBlogs);
  const status = useSelector(selectBlogsStatus);
  const error = useSelector(selectBlogsError);
  const totalPages = useSelector(selectBlogsTotalPages);
  const currentPage = useSelector(selectBlogsCurrentPage);
  const tags = useSelector(selectAllTags);

  // Fetch blogs on component mount and when page/tag changes
  useEffect(() => {
    dispatch(fetchBlogs({ page: currentPage, pageSize: 9, tag: selectedTag }));
  }, [dispatch, currentPage, selectedTag]);

  // Blog search functionality with debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        fetchSearchSuggestions(searchTerm);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const fetchSearchSuggestions = async (query) => {
    setIsSearching(true);
    setShowSuggestions(true);
    
    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/blogs/search/suggest/?q=${encodeURIComponent(query)}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch suggestions');
      
      const data = await response.json();
      
      if (data.success && Array.isArray(data.suggestions)) {
        setSearchSuggestions(data.suggestions);
      } else {
        setSearchSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching blog suggestions:', error);
      setSearchSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle tag filter
  const handleTagClick = (tag) => {
    setSelectedTag(tag === selectedTag ? "" : tag);
    dispatch(setCurrentPage(1));
  };

  // Handle search input
  const handleSearchInput = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle suggestion click
  const handleSuggestionClick = (blogId) => {
    navigate(`/Blogdetails/${blogId}`);
    setSearchTerm('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle search blur
  const handleSearchBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  // Handle search focus
  const handleSearchFocus = () => {
    if (searchSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim() && searchSuggestions.length > 0) {
      navigate(`/Blogdetails/${searchSuggestions[0].id}`);
      setSearchTerm('');
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    dispatch(setCurrentPage(page));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Format date helper
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Strip HTML tags and get plain text excerpt
  const getExcerpt = (content) => {
    if (!content) return "Read more about this topic...";
    
    // Create a temporary div to strip HTML tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up and truncate
    const cleanText = plainText.replace(/\s+/g, ' ').trim();
    return cleanText.length > 120 
      ? cleanText.substring(0, 120) + "..." 
      : cleanText;
  };

  // Get blog image with proper path
  const getBlogImage = (blog) => {
    if (blog.featured_image) {
      return blog.featured_image.startsWith('http') 
        ? blog.featured_image 
        : `https://evercorenutrition.com${blog.featured_image}`;
    }
    return '/assets/images/Yeast_Protein.png';
  };

  return (
    <>
      <Navbar />

      {/* Breadcrumb Section Start */}
      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>Blog Grid</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <a href="/">
                        <i className="fa-solid fa-house"></i>
                      </a>
                    </li>
                    <li className="breadcrumb-item active">Blog Grid</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Breadcrumb Section End */}

      {/* Blog Section Start */}
      <section className="blog-section section-b-space" style={{backgroundColor:"#f1f1f1"}}>
        <div className="container-fluid-lg">
          <div className="row g-4">
            <div className="col-xxl-9 col-xl-8 col-lg-7 order-lg-2">
              {/* Loading State */}
              {status === 'loading' && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              {/* Error State */}
              {status === 'failed' && (
                <div className="alert alert-danger" role="alert">
                  Error loading blogs: {error}
                </div>
              )}

              {/* Blog Grid */}
              {status === 'succeeded' && (
                <>
                  <div className="row g-4">
                    {blogs.length > 0 ? (
                      blogs.map((blog, index) => (
                        <div 
                          className="col-xxl-4 col-sm-6 mb-4" 
                         
                          key={blog.id}
                        >
                          <div 
                            className="blog-box wow fadeInUp h-100 d-flex flex-column" 
                             style={{boxShadow:"1px 2px 15px #d4d4d4"}}
                            data-wow-delay={index === 0 ? "0s" : index === 1 ? "0.05s" : "0.1s"}
                            style={{ 
                              height: "100%", 
                              backgroundColor: "#fff", 
                              borderRadius: "12px", 
                              overflow: "hidden", 
                              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                              transition: "transform 0.3s ease, box-shadow 0.3s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-5px)";
                              e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.12)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.08)";
                            }}
                          >
                            {/* Blog Image - Fixed size */}
                            {/* <div className="blog-image" style={{ 
                              width: "100%", 
                              height: "220px", 
                              overflow: "hidden",
                              backgroundColor: "#f5f5f5"
                            }}> */}

                            <div className="blog-image" style={{ 
  width: "100%", 
  aspectRatio: "1 / 1",   // ✅ PERFECT SQUARE
  overflow: "hidden",
  backgroundColor: "#f5f5f5"
}}>
                              <a href={`/Blogdetails/${blog.id}`}>
                                <img
                                  src={getBlogImage(blog)}
                                  className="img-fluid w-100 h-100"
                                  alt={blog.title}
                                  style={{ 
                                    width: "100%", 
                                    height: "100%",
                                    objectFit: "cover",
                                    transition: "transform 0.3s ease"
                                  }}
                                  onMouseEnter={(e) => {
                                    if (e.target.style.transform !== "scale(1.1)") {
                                      e.target.style.transform = "scale(1.05)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = "scale(1)";
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/assets/images/Yeast_Protein.png';
                                  }}
                                />
                              </a>
                            </div>

                            <div className="blog-contain d-flex flex-column flex-grow-1" style={{ padding: "20px" }}>
                              {/* Meta info */}
                              <div className="blog-label mb-3 d-flex justify-content-between align-items-center">
                                <span className="time" style={{ fontSize: "12px", color: "#666" }}>
                                  <i className="fa-regular fa-calendar me-1"></i> 
                                  <span>{formatDate(blog.publication_date || blog.created_at)}</span>
                                </span>
                                <span className="super" style={{ fontSize: "12px", color: "#666" }}>
                                  <i className="fa-regular fa-user me-1"></i>
                                  <span>{blog.author || "Evercore"}</span>
                                </span>
                              </div>
                              
                              {/* Blog Title */}
                              <a href={`/Blogdetails/${blog.id}`} className="text-decoration-none">
                                <h3 style={{ 
                                  fontSize: "18px", 
                                  fontWeight: "600",
                                  marginBottom: "12px",
                                  color: "#000",
                                  lineHeight: "1.4",
                                  minHeight: "50px",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden"
                                }}>
                                  {blog.title}
                                </h3>
                              </a>
                              
                              {/* Blog Excerpt - Fixed text area */}
                              <p className="text-content" style={{ 
                                fontSize: '14px', 
                                lineHeight: '1.6',
                                minHeight: "65px",
                                marginBottom: "20px",
                                color: "#666",
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden"
                              }}>
                                {getExcerpt(blog.content)}
                              </p>
                              
                              {/* Read More Button */}
                              <div className="mt-auto">
                                <a href={`/Blogdetails/${blog.id}`} style={{ textDecoration: "none" }}>
                                  <button className="blog-button" style={{ 
                                    background: "none", 
                                    border: "none", 
                                    color: "#000", 
                                    fontWeight: "600", 
                                    padding: "8px 0",
                                    fontSize: "14px",
                                    cursor: "pointer",
                                    transition: "color 0.3s ease"
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = "#2b7e2b"}
                                  onMouseLeave={(e) => e.currentTarget.style.color = "#000"}
                                  >
                                    Read More
                                    <i className="fa-solid fa-right-long ms-2"></i>
                                  </button>
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center py-5">
                        <h4>No blogs found</h4>
                        <p className="text-muted">Try adjusting your search or filter criteria</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && blogs.length > 0 && (
                    <nav className="custom-pagination mt-5">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <a
                            className="page-link"
                            href="javascript:void(0)"
                            onClick={() => handlePageChange(1)}
                          >
                            <i className="fa-solid fa-angles-left"></i>
                          </a>
                        </li>
                        
                        {[...Array(totalPages).keys()].map((num) => (
                          <li 
                            key={num + 1} 
                            className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}
                          >
                            <a 
                              className="page-link" 
                              href="javascript:void(0)"
                              onClick={() => handlePageChange(num + 1)}
                            >
                              {num + 1}
                            </a>
                          </li>
                        ))}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <a
                            className="page-link"
                            href="javascript:void(0)"
                            onClick={() => handlePageChange(totalPages)}
                          >
                            <i className="fa-solid fa-angles-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="col-xxl-3 col-xl-4 col-lg-5 order-lg-1" style={{boxShadow:"1px 2px 15px #c5c5c5", borderRadius:"10px", padding:"10px"}}>
              <div className="left-sidebar-box wow fadeInUp">
                {/* Search Box with Suggestions */}
                <div className="left-search-box">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="search-box1 search-box" style={{ position: 'relative', marginTop:"10px" }}>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search blogs..."
                        value={searchTerm}
                        onChange={handleSearchInput}
                        onFocus={handleSearchFocus}
                        onBlur={handleSearchBlur}
                        autoComplete="off"
                        style={{ paddingRight: '35px' }}
                      />
                      {isSearching && (
                        <div style={{ 
                          position: 'absolute', 
                          right: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          zIndex: 2
                        }}>
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Search Suggestions Dropdown */}
                      {showSuggestions && searchSuggestions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                          {searchSuggestions.map((suggestion) => (
                            <div
                              key={suggestion.id}
                              onClick={() => handleSuggestionClick(suggestion.id)}
                              style={{
                                padding: '12px 15px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                            >
                              <h6 style={{ margin: 0, fontSize: '14px', fontWeight: '500' }}>
                                {suggestion.title}
                              </h6>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {showSuggestions && searchTerm.length > 1 && searchSuggestions.length === 0 && !isSearching && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          padding: '15px',
                          textAlign: 'center',
                          color: '#666',
                          zIndex: 1000,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}>
                          No blogs found
                        </div>
                      )}
                    </div>
                  </form>
                </div>

                <div
                  className="accordion left-accordion-box"
                  id="accordionPanelsStayOpenExample"
                >
                  {/* Recent Posts */}
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="panelsStayOpen-headingOne">
                      <button
                        className="accordion-button"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#panelsStayOpen-collapseOne"
                      >
                        Recent Posts
                      </button>
                    </h2>
                    <div
                      id="panelsStayOpen-collapseOne"
                      className="accordion-collapse collapse show"
                    >
                      <div className="accordion-body pt-0">
                        <div className="recent-post-box">
                          {blogs.slice(0, 2).map((blog) => (
                            <div className="recent-box d-flex gap-3 mb-3" key={blog.id}>
                              <a href={`/Blogdetails/${blog.id}`} className="recent-image" style={{ flexShrink: 0 }}>
                                <img
                                  src={getBlogImage(blog)}
                                  className="img-fluid blur-up lazyload"
                                  alt={blog.title}
                                  style={{ 
                                    width: "70px", 
                                    height: "70px", 
                                    objectFit: "cover", 
                                    borderRadius: "8px" 
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/assets/images/Yeast_Protein.png';
                                  }}
                                />
                              </a>

                              <div className="recent-detail flex-grow-1">
                                <a href={`/Blogdetails/${blog.id}`} style={{ textDecoration: "none" }}>
                                  <h5 className="recent-name" style={{ 
                                    fontSize: "14px", 
                                    fontWeight: "600",
                                    marginBottom: "5px",
                                    color: "#000",
                                    lineHeight: "1.4"
                                  }}>
                                    {blog.title.length > 50 
                                      ? blog.title.substring(0, 50) + "..." 
                                      : blog.title}
                                  </h5>
                                </a>
                                <h6 className="text-muted small" style={{ fontSize: "11px", margin: 0 }}>
                                  {formatDate(blog.publication_date || blog.created_at)} 
                                </h6>
                              </div>
                            </div>
                          ))}
                          {blogs.length === 0 && (
                            <p className="text-muted small">No recent posts</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Product Tags */}
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="panelsStayOpen-headingThree">
                      <button
                        className="accordion-button collapsed"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#panelsStayOpen-collapseThree"
                      >
                        Product Tags
                      </button>
                    </h2>
                    <div
                      id="panelsStayOpen-collapseThree"
                      className="accordion-collapse collapse show"
                    >
                      <div className="accordion-body pt-0">
                        <div className="product-tags-box">
                          <ul className="d-flex flex-wrap gap-2" style={{ paddingLeft: 0 }}>
                            {tags.length > 0 ? (
                              tags.map((tag) => (
                                <li key={tag} style={{ listStyle: "none" }}>
                                  <a 
                                    href="javascript:void(0)" 
                                    onClick={() => handleTagClick(tag)}
                                    style={{ 
                                      backgroundColor: selectedTag === tag ? '#2b7e2b' : '#f0f0f0',
                                      color: selectedTag === tag ? '#fff' : '#333',
                                      padding: '5px 12px',
                                      borderRadius: '20px',
                                      fontSize: '13px',
                                      display: 'inline-block',
                                      textDecoration: 'none',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (selectedTag !== tag) {
                                        e.currentTarget.style.backgroundColor = '#e0e0e0';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (selectedTag !== tag) {
                                        e.currentTarget.style.backgroundColor = '#f0f0f0';
                                      }
                                    }}
                                  >
                                    {tag}
                                  </a>
                                </li>
                              ))
                            ) : (
                              <li style={{ listStyle: "none", color: "#666" }}>
                                No tags available
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Blog Section End */}
      <Footer />
    </>
  );
};

export default Blog;